import type { ReactNode } from 'react';

import type { InputHTMLAttributes } from 'types';
import { cnm } from 'utils/classNames';
import { empty } from 'utils/helpers';

import { getCheckboxRadioClassName, type RadioCheckboxColor } from './helpers';

// Omit onClick to enforce the use of onChange.
export interface Props extends Omit<
	InputHTMLAttributes<HTMLInputElement>,
	'onClick' | 'type'
> {
	color?: RadioCheckboxColor;
	/**
	 * Accessibly mark and display the field as disabled.
	 *
	 * NOTE! This results in an ARIA disable rather than a full, native disable
	 * which means a form submission will still include the value.
	 */
	disabled?: boolean;
	id: string;
	inputClassName?: string;
	label: ReactNode;
	labelClassName?: string;
	name: string;
}

export default function Radio({
	className,
	color = 'green',
	disabled,
	id,
	inputClassName,
	label,
	labelClassName,
	onChange,
	...attrs
}: Props) {
	return (
		<div className={cnm('flex items-start', className)}>
			<input
				{...attrs}
				type="radio"
				className={getCheckboxRadioClassName({
					color,
					disabled,
					extra: inputClassName,
				})}
				onChange={disabled ? empty.func : onChange}
				aria-disabled={disabled ? 'true' : undefined}
				id={id}
			/>
			<label
				htmlFor={id}
				className={cnm(
					'pl-2',
					!disabled && 'cursor-pointer',
					// Disable pointer events to stop the radio from showing mouse
					// feedback, since it's only ARIA disabled.
					disabled && 'pointer-events-none cursor-default opacity-50',
					labelClassName,
				)}
			>
				{label}
			</label>
		</div>
	);
}
Radio.displayName = 'FormUI_Radio';
